import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, CloudUpload, History, Update } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';

const TRAINING_TYPES = ['INTERNAL', 'EXTERNAL', 'MANDATORY', 'REFRESHER', 'OTHER'];
const TRAINING_STATUSES = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const STATUS_COLOR = {
  ASSIGNED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'default'
};

function formatDate(value) {
  return value ? dayjs(value).format('YYYY-MM-DD') : '—';
}

function employeeLabel(user) {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

/**
 * HRMS Training — HR/SA assign time-bound trainings to bands, groups, and/or individuals.
 * Employees mark completion and may upload a certificate.
 */
function TrainingPanel({
  employees = [],
  canAssignTraining,
  canAssign,
  department,
  from,
  to,
  currentUserId
}) {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [bands, setBands] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [assignForm, setAssignForm] = useState({
    userIds: [],
    bandIds: [],
    groupIds: [],
    title: '',
    trainingType: 'INTERNAL',
    description: '',
    provider: '',
    location: '',
    dueDate: '',
    startDate: '',
    requireCertificate: false,
    remarks: ''
  });
  const [updateForm, setUpdateForm] = useState({
    status: 'IN_PROGRESS',
    note: '',
    completedDate: dayjs().format('YYYY-MM-DD'),
    certificateFile: null
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (department) params.department = department;
      if (statusFilter) params.status = statusFilter;
      const [trainRes, bandRes, groupRes] = await Promise.all([
        axios.get('/api/hrms/trainings', { params }),
        axios.get('/api/hrms/bands', { params: { activeOnly: 'true' } }).catch(() => ({ data: [] })),
        axios.get('/api/hrms/groups', { params: { activeOnly: 'true' } }).catch(() => ({ data: [] }))
      ]);
      setRows(trainRes.data || []);
      setBands(bandRes.data || []);
      setGroups(groupRes.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load trainings', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, department, statusFilter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const previewAssignees = useMemo(() => {
    const ids = new Set(assignForm.userIds.map(String));
    for (const band of bands) {
      if (!assignForm.bandIds.map(String).includes(String(band._id))) continue;
      for (const m of band.members || []) {
        const uid = m.userId?._id || m.userId;
        if (uid) ids.add(String(uid));
      }
    }
    for (const group of groups) {
      if (!assignForm.groupIds.map(String).includes(String(group._id))) continue;
      for (const m of group.members || []) {
        const uid = m.userId?._id || m.userId;
        if (uid) ids.add(String(uid));
      }
    }
    return ids.size;
  }, [assignForm.userIds, assignForm.bandIds, assignForm.groupIds, bands, groups]);

  const openAssign = () => {
    setAssignForm({
      userIds: [],
      bandIds: [],
      groupIds: [],
      title: '',
      trainingType: 'INTERNAL',
      description: '',
      provider: '',
      location: '',
      dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
      startDate: dayjs().format('YYYY-MM-DD'),
      requireCertificate: false,
      remarks: ''
    });
    setAssignOpen(true);
  };

  const openUpdate = (row) => {
    setSelected(row);
    const isSelf = String(row.userId?._id || row.userId) === String(currentUserId);
    setUpdateForm({
      status: row.status === 'COMPLETED' || row.status === 'CANCELLED'
        ? row.status
        : (isSelf && !canAssignTraining ? 'COMPLETED' : (row.status === 'ASSIGNED' ? 'IN_PROGRESS' : row.status)),
      note: '',
      completedDate: dayjs().format('YYYY-MM-DD'),
      certificateFile: null
    });
    setUpdateOpen(true);
  };

  const openHistory = async (row) => {
    try {
      const res = await axios.get(`/api/hrms/trainings/${row._id}`);
      setSelected(res.data);
      setHistoryOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load training history', 'error');
    }
  };

  const onAssign = async () => {
    if (!assignForm.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    if (!assignForm.dueDate) {
      showToast('Due date is required', 'error');
      return;
    }
    if (!assignForm.userIds.length && !assignForm.bandIds.length && !assignForm.groupIds.length) {
      showToast('Select at least one band, group, and/or individual', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: assignForm.title,
        trainingType: assignForm.trainingType,
        description: assignForm.description || undefined,
        provider: assignForm.provider || undefined,
        location: assignForm.location || undefined,
        dueDate: assignForm.dueDate,
        startDate: assignForm.startDate || undefined,
        requireCertificate: assignForm.requireCertificate,
        remarks: assignForm.remarks || undefined,
        userIds: assignForm.userIds,
        bandIds: assignForm.bandIds,
        groupIds: assignForm.groupIds
      };
      const res = await axios.post('/api/hrms/trainings', payload);
      const count = Array.isArray(res.data) ? res.data.length : 1;
      showToast(`Training assigned to ${count} employee(s)`, 'success');
      setAssignOpen(false);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Assign failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onUpdate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const needsCert = updateForm.status === 'COMPLETED'
        && (selected.requireCertificate || updateForm.certificateFile);

      if (updateForm.certificateFile || (needsCert && updateForm.certificateFile)) {
        const form = new FormData();
        if (updateForm.certificateFile) {
          form.append('certificate', updateForm.certificateFile);
        }
        if (updateForm.status === 'COMPLETED') {
          form.append('complete', 'true');
          form.append('note', updateForm.note || 'Completed with certificate');
          form.append('completedDate', updateForm.completedDate || dayjs().format('YYYY-MM-DD'));
        }
        await axios.post(`/api/hrms/trainings/${selected._id}/certificate`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (updateForm.status !== 'COMPLETED' && updateForm.certificateFile) {
          await axios.post(`/api/hrms/trainings/${selected._id}/updates`, {
            status: updateForm.status,
            note: updateForm.note
          });
        }
      } else {
        const payload = {
          status: updateForm.status,
          note: updateForm.note
        };
        if (updateForm.status === 'COMPLETED') {
          payload.completedDate = updateForm.completedDate || dayjs().format('YYYY-MM-DD');
        }
        await axios.post(`/api/hrms/trainings/${selected._id}/updates`, payload);
      }
      showToast('Training updated', 'success');
      setUpdateOpen(false);
      setSelected(null);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = async (row) => {
    try {
      await axios.post(`/api/hrms/trainings/${row._id}/cancel`, {
        note: 'Cancelled by HR'
      });
      showToast('Training cancelled', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Cancel failed', 'error');
    }
  };

  const canUpdateRow = useMemo(() => {
    return (row) => {
      if (!row) return false;
      if (canAssignTraining || canAssign) return true;
      return String(row.userId?._id || row.userId) === String(currentUserId)
        && row.status !== 'COMPLETED'
        && row.status !== 'CANCELLED';
    };
  }, [canAssignTraining, canAssign, currentUserId]);

  const statusOptionsForUpdate = useMemo(() => {
    if (canAssignTraining) return TRAINING_STATUSES;
    return ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED'];
  }, [canAssignTraining]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        spacing={2}
        mb={2.5}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Time-bound assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Assign trainings to bands, groups, or people. Track progress, then upload a completion
            certificate from Update when required.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {canAssignTraining && (
            <Button variant="contained" startIcon={<Add />} onClick={openAssign}>
              Assign training
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {TRAINING_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Certificate</TableCell>
              <TableCell>Assigned by</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const overdue = row.dueDate
                && row.status !== 'COMPLETED'
                && row.status !== 'CANCELLED'
                && dayjs(row.dueDate).isBefore(dayjs(), 'day');
              return (
                <TableRow key={row._id} hover>
                  <TableCell>{employeeLabel(row.userId)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.title}</Typography>
                    {row.requireCertificate ? (
                      <Typography variant="caption" color="warning.main">Cert required</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.trainingType}</TableCell>
                  <TableCell>
                    {formatDate(row.startDate)} → {formatDate(row.dueDate)}
                  </TableCell>
                  <TableCell>
                    {formatDate(row.dueDate)}
                    {overdue && (
                      <Chip size="small" color="error" label="Overdue" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status.replace(/_/g, ' ')}
                      color={STATUS_COLOR[row.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {row.certificateFileUrl ? (
                      <Button
                        size="small"
                        href={row.certificateFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{employeeLabel(row.assignedBy)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        startIcon={<History />}
                        onClick={() => openHistory(row)}
                      >
                        History
                      </Button>
                      {canUpdateRow(row) && (
                        <Button
                          size="small"
                          startIcon={<Update />}
                          onClick={() => openUpdate(row)}
                        >
                          Update
                        </Button>
                      )}
                      {canAssignTraining && row.status !== 'CANCELLED' && row.status !== 'COMPLETED' && (
                        <Button size="small" color="error" onClick={() => onCancel(row)}>
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={9}>
                  {loading ? 'Loading…' : 'No training assignments in this range'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Assign time-bound training</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              1 · Who receives it
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="train-bands-label">Bands (optional)</InputLabel>
              <Select
                labelId="train-bands-label"
                multiple
                value={assignForm.bandIds}
                onChange={(e) => setAssignForm({ ...assignForm, bandIds: e.target.value })}
                input={<OutlinedInput label="Bands (optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const band = bands.find((b) => String(b._id) === String(id));
                      return <Chip key={id} size="small" label={band?.name || id} />;
                    })}
                  </Box>
                )}
              >
                {bands.map((band) => (
                  <MenuItem key={band._id} value={band._id}>
                    <Checkbox checked={assignForm.bandIds.map(String).includes(String(band._id))} />
                    <ListItemText
                      primary={band.name}
                      secondary={`${(band.members || []).length} members`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="train-groups-label">Groups (optional)</InputLabel>
              <Select
                labelId="train-groups-label"
                multiple
                value={assignForm.groupIds}
                onChange={(e) => setAssignForm({ ...assignForm, groupIds: e.target.value })}
                input={<OutlinedInput label="Groups (optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const group = groups.find((g) => String(g._id) === String(id));
                      return <Chip key={id} size="small" label={group?.name || id} />;
                    })}
                  </Box>
                )}
              >
                {groups.map((group) => (
                  <MenuItem key={group._id} value={group._id}>
                    <Checkbox checked={assignForm.groupIds.map(String).includes(String(group._id))} />
                    <ListItemText
                      primary={group.name}
                      secondary={`${(group.members || []).length} members${group.department ? ` · ${group.department}` : ''}`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="train-users-label">Individuals (optional)</InputLabel>
              <Select
                labelId="train-users-label"
                multiple
                value={assignForm.userIds}
                onChange={(e) => setAssignForm({ ...assignForm, userIds: e.target.value })}
                input={<OutlinedInput label="Individuals (optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const emp = employees.find((e) => String(e._id) === String(id));
                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={emp ? `${emp.firstName} ${emp.lastName}` : id}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {employees.map((e) => (
                  <MenuItem key={e._id} value={e._id}>
                    <Checkbox checked={assignForm.userIds.map(String).includes(String(e._id))} />
                    <ListItemText
                      primary={`${e.firstName} ${e.lastName}`}
                      secondary={e.department || e.role}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info">
              Select bands, groups, individuals, or any combination. Unique assignees:{' '}
              <strong>{previewAssignees}</strong>
            </Alert>

            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              2 · Training details
            </Typography>
            <TextField
              label="Title"
              required
              fullWidth
              value={assignForm.title}
              onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={assignForm.trainingType}
                onChange={(e) => setAssignForm({ ...assignForm, trainingType: e.target.value })}
              >
                {TRAINING_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={assignForm.startDate}
                onChange={(e) => setAssignForm({ ...assignForm, startDate: e.target.value })}
              />
              <TextField
                label="Due date"
                type="date"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={assignForm.dueDate}
                onChange={(e) => setAssignForm({ ...assignForm, dueDate: e.target.value })}
              />
            </Stack>
            <TextField
              label="Provider"
              fullWidth
              value={assignForm.provider}
              onChange={(e) => setAssignForm({ ...assignForm, provider: e.target.value })}
            />
            <TextField
              label="Location"
              fullWidth
              value={assignForm.location}
              onChange={(e) => setAssignForm({ ...assignForm, location: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={assignForm.description}
              onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })}
            />

            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              3 · Completion &amp; certificate
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={assignForm.requireCertificate}
                    onChange={(e) => setAssignForm({
                      ...assignForm,
                      requireCertificate: e.target.checked
                    })}
                  />
                )}
                label="Require certificate upload when the employee marks this complete"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                When checked, Update → Complete will ask for a PDF/JPG/PNG certificate file.
              </Typography>
            </Paper>
            <TextField
              label="Remarks"
              fullWidth
              value={assignForm.remarks}
              onChange={(e) => setAssignForm({ ...assignForm, remarks: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onAssign} disabled={saving}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Update training
          {selected ? ` · ${selected.title}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              1 · Progress
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={updateForm.status}
                onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
              >
                {statusOptionsForUpdate.map((s) => (
                  <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {updateForm.status === 'COMPLETED' && (
              <TextField
                label="Completed date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={updateForm.completedDate}
                onChange={(e) => setUpdateForm({ ...updateForm, completedDate: e.target.value })}
              />
            )}
            <TextField
              label="Update note"
              fullWidth
              multiline
              minRows={2}
              value={updateForm.note}
              onChange={(e) => setUpdateForm({ ...updateForm, note: e.target.value })}
            />

            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              2 · Certificate upload
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor:
                  selected?.requireCertificate || updateForm.status === 'COMPLETED'
                    ? 'rgba(237, 108, 2, 0.08)'
                    : 'grey.50'
              }}
            >
              {(updateForm.status === 'COMPLETED' || selected?.requireCertificate) ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    {selected?.requireCertificate
                      ? 'This assignment requires a completion certificate.'
                      : 'Optional: attach a certificate when marking complete.'}
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                    color={updateForm.certificateFile ? 'success' : 'primary'}
                  >
                    {updateForm.certificateFile
                      ? updateForm.certificateFile.name
                      : (selected?.requireCertificate
                        ? 'Upload certificate (required)'
                        : 'Upload certificate (optional)')}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,image/jpeg,image/png"
                      onChange={(e) => setUpdateForm({
                        ...updateForm,
                        certificateFile: e.target.files?.[0] || null
                      })}
                    />
                  </Button>
                  {selected?.certificateFileUrl && !updateForm.certificateFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Existing certificate on file —{' '}
                      <Button
                        size="small"
                        href={selected.certificateFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      >
                        view
                      </Button>
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Set status to Completed (or when certificate is required) to upload a file here.
                </Typography>
              )}
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onUpdate} disabled={saving}>
            Save update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Training history
          {selected ? ` · ${selected.title}` : ''}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {employeeLabel(selected.userId)} · due {formatDate(selected.dueDate)}
                {selected.requireCertificate ? ' · certificate required' : ''}
              </Typography>
              {selected.certificateFileUrl && (
                <Button
                  size="small"
                  href={selected.certificateFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open certificate
                </Button>
              )}
              <Divider />
              <Typography variant="subtitle2">Updates</Typography>
              {(selected.updates || []).slice().reverse().map((u) => (
                <Paper key={u._id || `${u.updatedAt}-${u.status}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={(u.status || '').replace(/_/g, ' ')}
                      color={STATUS_COLOR[u.status] || 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {u.updatedAt ? dayjs(u.updatedAt).format('YYYY-MM-DD HH:mm') : ''}
                      {u.updatedBy ? ` · ${employeeLabel(u.updatedBy)}` : ''}
                    </Typography>
                  </Stack>
                  {u.note && (
                    <Typography variant="body2" sx={{ mt: 1 }}>{u.note}</Typography>
                  )}
                </Paper>
              ))}
              {!selected.updates?.length && (
                <Typography variant="body2" color="text.secondary">No updates yet</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrainingPanel;
